/**
 * Confirmation modal shown when the user types a brand-new CPT description
 * into the searchable-card cell on the CPT codes screen. Reads the candidate
 * description, row identifier, and current J-code from Redux modal metadata
 * (set by the page's `onCreateOption` handler) and surfaces a Yes/No confirm.
 *
 * @module cptNewDescriptionModal
 */
import { Modal } from "@risalabs_frontend_org/oasis-ui-kit";
import { useSelector } from "react-redux";
import { ModalId } from "../../../enums/modalId";
import { RootState } from "../../../redux/store/store";
import OutlineWarningIcon from "../../../svg/outline-warning-icon";

const BASE_CLASS_NAME = "cpt-new-description-modal";

/** Props for {@link CptNewDescriptionModal}. */
export interface CptNewDescriptionModalProps {
  /** Fired when the user dismisses the modal via the cancel button. */
  onCancel: () => void;
  /** Fired when the user dismisses the modal via the close affordance. */
  onClose: () => void;
  /**
   * Fired when the user confirms the new description. Receives the
   * description text, the originating row id, and the row's J-code
   * (all sourced from Redux modal metadata).
   */
  onSave: (description: string, id: string, cptCode: string) => void;
}

/** Shape of the Redux modal metadata consumed by {@link CptNewDescriptionModal}. */
export interface CptNewDescriptionModalMetaData {
  /** Free-text description being confirmed. */
  description?: string;
  /** Originating row id (matches `SearchableCardCellValue.id`). */
  id?: string;
  /** Current J-code for the row, forwarded into the save payload. */
  cptCode?: string;
}

/**
 * Renders the create-new-description confirmation dialog. Pulls
 * `description`, `id`, and `cptCode` from Redux modal metadata and forwards
 * them to `onSave` when the user confirms.
 */
const CptNewDescriptionModal = ({
  onCancel,
  onClose,
  onSave,
}: CptNewDescriptionModalProps) => {
  const { metaData } = useSelector((state: RootState) => state.modal);
  const { description, id, cptCode } =
    metaData as CptNewDescriptionModalMetaData;
  return (
    <Modal
      dialogId={ModalId.CPT_NEW_DESCRIPTION_MODAL}
      title="Create New Description"
      onCancel={onCancel}
      onClose={onClose}
      onSave={() => onSave(description ?? "", id ?? "", cptCode ?? "")}
      saveButtonText="Yes, Confirm"
      cancelText={"No, Cancel"}
    >
      <div
        className={`${BASE_CLASS_NAME}__container flex flex-col items-center justify-center gap-4 py-2`}
      >
        <div
          className={`${BASE_CLASS_NAME}__icon flex h-14 w-14 items-center justify-center rounded-full bg-primaryGray-16`}
        >
          <OutlineWarningIcon />
        </div>
        <div
          className={`${BASE_CLASS_NAME}__title text-center font-normal text-primaryGray-1`}
        >
          Are you sure you want to create this description ?
        </div>
        <div
          className={`${BASE_CLASS_NAME}__description h-10 w-full truncate rounded-[0.25rem] bg-primaryGray-16 px-1 py-2 text-center text-body font-semibold text-primaryGray-1`}
        >
          {description ?? ""}
        </div>
      </div>
    </Modal>
  );
};

export default CptNewDescriptionModal;
